import { ProjectApi } from "@api/project-api/project-api";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  CancelRegistrationMultiCloudResponse,
  CaptureApiClient,
  CaptureTreeEntity,
  createMutationDeleteCaptureTreeEntities,
  RegistrationApiClient,
  RegistrationState,
} from "@faro-lotv/service-wires";
import { AppDispatch } from "@store/store-helper";
import { isStagingAreaUploadTaskContext } from "@custom-types/file-upload-type-guards";
import { ReturnFunction } from "@hooks/data-management/use-cancel-revision";
import { FileUploadTask } from "@custom-types/file-upload-types";
import { UploadManagerInterface } from "@custom-types/upload-manager-types";
import { isTaskInProgress } from "@hooks/upload-tasks/upload-tasks-utils";
import { LogEventParams } from "@utils/track-event/use-track-event";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { changeSummaryForTracking } from "@utils/capture-tree/capture-tree-changes";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { sentryCaptureError } from "@src/utils/sentry-utils";
import { fetchCaptureTreeData, fetchRevisionsAndDraftEntities } from "@hooks/data-management/use-data-management";
import { ProgressApiClient } from "@api/progress-api/progress-api-client";
import { ShowToastProps } from "@hooks/use-toast";
import { SnackbarKey } from "notistack";
import { ErrorHandlingFunction } from "@context-providers/error-boundary/error-handling-context";
import { summarizeRevisionChanges, wasDraftUpdated } from "@utils/capture-tree/capture-tree-changes";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { CreateDialogProps } from "@components/common/dialog/dialog-provider";
import React from "react";
import { Typography } from "@mui/material";

/** Delete entities from Capture Tree, using a mutation. */
export async function deleteCaptureTreeEntities(projectApiClient: ProjectApi, entities: CaptureTreeEntity[]): Promise<void> {
  assert(entities.length > 0, "No entities to delete.");
  // Remove scans from capture tree.
  const mutation = createMutationDeleteCaptureTreeEntities(
    entities.map((entity) => entity.id),
    CaptureApiClient.dashboard
  );
  await projectApiClient.applyMutations([mutation]);
}

/**
 * Cancels the upload step and removes the tasks from the store.
 */
export async function cancelUploads(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  progressApiClient: ProgressApiClient,
  handleErrorWithToast: ErrorHandlingFunction,
  setCancelInProgress: React.Dispatch<React.SetStateAction<boolean>>,
  captureTreeRevisionId: GUID,
  cancelRevision: ReturnFunction,
  uploadTasks: FileUploadTask[],
  uploadManager: UploadManagerInterface,
  trackEvent: (params: LogEventParams) => void,
  isFromErrorView: boolean,
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> {
  setCancelInProgress(true);
  let hasOperationFailed = false;
  let revision, tasksCanceledTotal, tasksCanceledInProgress;

  try {
    if (setShouldPreventRedirect) {
      setShouldPreventRedirect(true);
    }

    // If the user has opened the dialog shortly before the upload has finished, it might now be too late to cancel.
    // Canceling the revision first makes sure that useAddScansToRevisionAndMerge() can detect it.
    revision = await projectApiClient.getRegistrationRevision(captureTreeRevisionId);

    if (revision.state === RegistrationState.started) {
      await cancelRevision(projectApiClient, captureTreeRevisionId);
    }

    // We also need to cancel aborted and successful tasks, otherwise they would stay on the page.
    // E.g. smaller scans might be successful before the user cancels the upload.
    [tasksCanceledTotal, tasksCanceledInProgress] = cleanupUploadTasks(uploadTasks, uploadManager);
  } catch (error) {
    hasOperationFailed = true;
    handleErrorWithToast({
      id: `onCancelUpload-${Date.now().toString()}`,
      title: "Failed to cancel the upload process. Please try reloading the page.",
      error,
    });
  } finally {
    trackEvent({
      name: DataManagementEvents.cancelImport,
      props: {
        workflowState: "upload",
        tasksCanceledTotal: tasksCanceledTotal ?? 0,
        tasksCanceledInProgress: tasksCanceledInProgress ?? 0,
        captureTreeRevisionId,
        revisionState: revision?.state ?? "",
        isFromErrorView,
        hasOperationFailed,
      },
    });

    // Fetch the new state immediately instead of waiting until the next usePolling() happens.
    try {
      await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
    } catch { /* Not essential. The UI will update a bit later. */ }

    setCancelInProgress(false);
  }
}

async function cancelRegistrations(
  registrationApiClient: RegistrationApiClient,
  isRegistering: boolean
): Promise<CancelRegistrationMultiCloudResponse | undefined> {
  // Let's avoid wasting computing resources and terminate registration workers
  let jobsTerminated;
  // The user doesn't need to know if this fails nor succeeds
  try {
    // If the DELETE call is successful, the ongoing registration is canceled by the terminated job(s).
    // Stop worker task(s) through the MultiCloud Registration Trigger
    // Setting revisionId=* will terminate all running jobs for the given project.
    jobsTerminated = await registrationApiClient.cancelMultiCloudRegistration({ revisionId: "*" });
  } catch (error) {
    if (isRegistering) {
      // this shouldn't happen so let's report it
      sentryCaptureError({ error, title: "RegistrationError: Error terminating registration workers." });
    }
  }
  if (jobsTerminated?.errors.length) {
    sentryCaptureError({
      error: `Jobs not terminated: ${JSON.stringify(jobsTerminated.errors)}`,
      title: "RegistrationError: Not all workers got terminated",
    });
  }

  return jobsTerminated;
}

/**
 * Cancels the upload tasks and removes them from the store.
 */
export function cleanupUploadTasks(
  uploadTasks: FileUploadTask[],
  uploadManager: UploadManagerInterface
): [number, number] {
  const tasksToCancel = uploadTasks.filter((task) => isStagingAreaUploadTaskContext(task.context));
  const tasksToCancelInProgress = tasksToCancel.filter((task) => isTaskInProgress(task));

  for (const task of tasksToCancel) {
    // Cancel upload (if in progress), and remove from store (always).
    uploadManager.cancelFileUpload(task.id, true);
  }

  // Return the number of canceled tasks. For Amplitude event.
  return [tasksToCancel.length, tasksToCancelInProgress.length];
}

/**
 * Asks for confirmation, cancels any running registration tasks, and cancels the current draft revision (if shouldUseDiscard).
 */
export async function onCancelImport(
  dispatch: AppDispatch,
  projectApiClient: ProjectApi,
  progressApiClient: ProgressApiClient,
  registrationApiClient: RegistrationApiClient,
  showToast: (params: ShowToastProps) => SnackbarKey,
  handleErrorWithToast: ErrorHandlingFunction,
  shouldUseDiscard: boolean,
  createDialog: (options: CreateDialogProps, content?: JSX.Element | string) => Promise<boolean>,
  setCancelInProgress: React.Dispatch<React.SetStateAction<boolean>>,
  workflowState: WorkflowState,
  uploadTasks: FileUploadTask[],
  uploadManager: UploadManagerInterface,
  cancelRevision: ReturnFunction,
  trackEvent: (params: LogEventParams) => void,
  isRegistering: boolean,
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>,
  isLoading?: boolean,
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>,
  setIsRevertBtnClicked?: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> {
     function showOutdatedToast(): void {
    showToast({
      message: "The project has been updated by another user or application. Please review the changes and try again.",
      type: "warning",
    });
  }

  /** Handle an error, returning `undefined`, to make the error handling a bit shorter. */
  function showFetchErrorToast(error: unknown): void {
    handleErrorWithToast({
      id: `onCancelImport-fetch-${Date.now().toString()}`,
      title: "Failed to fetch the latest state of the project. Please try reloading the page.",
      error,
    });
  }

  setCancelInProgress(true);
  // switch to dropzone view inorder to show the spinner
  if (setIsRevertBtnClicked){
    setIsRevertBtnClicked(true);
  }

  // show the spinner in dropzone view
  if (isLoading === false && setIsLoading){
    setIsLoading(true);
  }

  let hasOperationFailed = false;
  let hasDeclined = false;
  let changeSummary, jobsTerminated;

  try {
    const beforeConfirm = await fetchRevisionsAndDraftEntities(dispatch, projectApiClient)
      .catch(showFetchErrorToast);
    if (!beforeConfirm) {
      // We came here from `showFetchErrorToast`, which returns undefined.
      hasOperationFailed = true;
      return;
    }

    const { openDraftRevision, openDraftEntities } = beforeConfirm;
    changeSummary = summarizeRevisionChanges(openDraftEntities);
    // If we're not discarding the draft, we don't have to care if there's a draft.
    // E.g. the user might have re-published and then wants to cancel the registration, before worker has created a revision.
    if (shouldUseDiscard && (!openDraftRevision || openDraftEntities.length === 0 || changeSummary.sum === 0)) {
      hasOperationFailed = true;
      showOutdatedToast();
      return;
    }

    const title = shouldUseDiscard ? "Discard Draft?" : "Cancel Import?";
    const confirmText = shouldUseDiscard ? "Discard Draft" : "Cancel Import";
    const closeText = shouldUseDiscard ? "Cancel" : "Continue Import";
    const msg = shouldUseDiscard ?
      "You are about to discard the project's draft version, and revert these draft changes:" :
      "You are about to interrupt the current import process.";

    // Show the changes only if we are discarding the draft.
    const changes = shouldUseDiscard && changeSummary.textJsx.map((change, i) =>
      <li key={i}>{change}</li>
    );
    if (!(await createDialog(
      {
        title,
        confirmText,
        closeText,
      },
      <Typography
        // <ul> is not allowed below <p>.
        component={"div"}
        sx={{
          fontSize: "14px",
          marginBottom: SPACE_ELEMENTS_OF_MODAL,
        }}
      >
        {msg}
        <ul>
          {changes}
        </ul>
      </Typography>
    ))) {
      hasDeclined = true;
      return;
    }

    const afterConfirm = await fetchRevisionsAndDraftEntities(dispatch, projectApiClient)
      .catch(showFetchErrorToast);
    if (!afterConfirm) {
      // We came here from `showFetchErrorToast`, which returns undefined.
      hasOperationFailed = true;
      return;
    }

    if (shouldUseDiscard && openDraftRevision &&
      (!afterConfirm.openDraftRevision ||
      wasDraftUpdated(openDraftRevision, openDraftEntities, afterConfirm.openDraftRevision, afterConfirm.openDraftEntities))
    ) {
      hasOperationFailed = true;
      showOutdatedToast();
      return;
    }

    try {
      if (setShouldPreventRedirect) {
        setShouldPreventRedirect(true);
      }
      jobsTerminated = await cancelRegistrations(registrationApiClient, isRegistering);
      if (shouldUseDiscard && afterConfirm.openDraftRevision) {
        // This should be done last, otherwise ProjectAPI might automatically create a new draft revision
        // if there are still other open revisions.
        await cancelRevision(projectApiClient, afterConfirm.openDraftRevision.id);
        // We also need to remove upload tasks, otherwise they would stay on the page.
        cleanupUploadTasks(uploadTasks, uploadManager);
      }
    } catch (error) {
      hasOperationFailed = true;
      handleErrorWithToast({
        id: `onCancelImport-${Date.now().toString()}`,
        title: "Failed to cancel the import process. Please try reloading the page.",
        error,
      });
    }
  } catch (error) {
    hasOperationFailed = true;
    throw error;
  } finally {
    if (!hasDeclined) {
      trackEvent({
        name: DataManagementEvents.cancelImport,
        props: {
          isCancelRegistrations: true,
          workflowState,
          jobsTerminatedSuccessful: jobsTerminated?.successful.length ?? 0,
          jobsTerminatedError: jobsTerminated?.errors.length ?? 0,
          changeSummary: changeSummary ? changeSummaryForTracking(changeSummary) : {},
          hasOperationFailed,
        },
      });

      // Fetch the new state immediately instead of waiting until the next usePolling() happens.
      try {
        await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
      } catch { /* Not essential. The UI will update a bit later. */ }
    }

    setCancelInProgress(false);
    // stop the loader in Dropzone view
    if (setIsLoading && setIsRevertBtnClicked){
      setIsLoading(false);
      // stop viewMode override.
      setIsRevertBtnClicked(false);
    }
  }
}
